import {
  ApplicationRef,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  Injectable,
  Injector,
  inject
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import {
  ApiService,
  Category,
  ImageHelper,
  Lobby,
  LobbySetting,
  Locale,
  LocaleSetting,
  StorageService,
  StorageType,
  defaultLocaleKey,
  electronSystemLocaleKey,
  navigatorLocaleKey,
  supportedLocalesKey,
  userSelectedLocaleKey
} from '@rct/lynx-lobbies-shared-lib';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  take,
  tap
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CategoryContainerComponent } from '../../pages/home/category/category-container/category-container.component';
import { LYNX_STORAGE_KEYS } from '../constants/lynx-storage-keys.constant';
import { FaviconTitle, FaviconTitleMap } from '../interfaces/favicon-title';
import { LynxGameService } from '../services/game.service';
import { LynxLocaleSettingService } from './locale-setting.service';
import { LynxLocalizeRouterService } from './localize-router.service';
import { LynxModalService } from './modal.service';

@Injectable({
  providedIn: 'root'
})
export class LynxLobbyService {
  private _storageService: StorageService = inject(StorageService);
  private _apiService: ApiService = inject(ApiService);
  private _translateService: TranslateService = inject(TranslateService);
  private _localizeRouterService: LynxLocalizeRouterService = inject(
    LynxLocalizeRouterService
  );
  private _titleService: Title = inject(Title);
  private _gameService: LynxGameService = inject(LynxGameService);
  private _modalService: LynxModalService = inject(LynxModalService);
  private _componentFactoryResolver: ComponentFactoryResolver = inject(
    ComponentFactoryResolver
  );
  private _injector: Injector = inject(Injector);
  private _appRef: ApplicationRef = inject(ApplicationRef);
  private _localeSettingService: LynxLocaleSettingService = inject(
    LynxLocaleSettingService
  );

  private _themeSubject: BehaviorSubject<LobbySetting> = new BehaviorSubject(
    undefined
  );
  private _lobbyTemplateIdSubject: BehaviorSubject<string> =
    new BehaviorSubject(undefined);
  private _categorySubject: BehaviorSubject<Category[]> = new BehaviorSubject(
    []
  );
  private _isDefaultLanguageLoaded: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  private _lobbyId: string;
  private _faviconTitleMap: FaviconTitleMap;
  private _lastLocale: Locale;

  get theme$(): Observable<LobbySetting> {
    return this._themeSubject.pipe(filter((x) => !!x));
  }

  get lobbyTemplateId$(): Observable<string> {
    return this._lobbyTemplateIdSubject.pipe(filter((x) => !!x));
  }

  get lobbyId(): string {
    return this._lobbyId;
  }

  get categories$(): Observable<Category[]> {
    return this._categorySubject.pipe(
      mergeMap((categories: Category[]) => {
        return of(
          categories[
            this._translateService.currentLang ||
              this._translateService.defaultLang
          ]
        );
      }),
      filter((categories) => !!categories)
    );
  }

  get isDefaultLanguageLoaded$(): Observable<boolean> {
    return this._isDefaultLanguageLoaded.asObservable();
  }

  get locale(): Locale {
    let storedLocale: Locale | string | undefined =
      this._storageService.get(LYNX_STORAGE_KEYS.locale, StorageType.Session) ??
      environment.locale;

    if (storedLocale) {
      storedLocale =
        this._localeSettingService.convertToLocale(storedLocale) ??
        environment.locale;
    }

    return storedLocale as Locale;
  }

  checkTheme(): void {
    const theme = this._storageService.get(
      LYNX_STORAGE_KEYS.lobbyTheme,
      StorageType.Session
    );
    if (theme) {
      this._themeSubject.next(theme);
    }
  }

  checkLobbyTemplateId(): void {
    const lobbyTemplateId = this._storageService.get(
      LYNX_STORAGE_KEYS.lobbyTemplateId,
      StorageType.Session
    );
    if (lobbyTemplateId) {
      this._lobbyTemplateIdSubject.next(lobbyTemplateId);
    }
  }

  getLobbyDetails$(
    activatedRouteUrl: string,
    onError?: (err: any) => void,
    deactivateLoadingOnEnd: boolean = true
  ): Observable<Lobby> {
    this._getFaviconTitle();

    if (this._lastLocale === this.locale) {
      return of(null);
    }

    this._lastLocale = this.locale;

    this._modalService.activateSolidModalBg();
    this._modalService.showLoading();

    return this._apiService.getLobbyDetails$().pipe(
      filter((res: Lobby) => !!res),
      map((lobbyDetails: Lobby) => {
        lobbyDetails.setting.supportedLocales =
          lobbyDetails.setting.supportedLocales.map((x) =>
            this._localeSettingService.convertToLocale(x)
          );
        return lobbyDetails;
      }),
      tap((lobbyDetails: Lobby) => {
        this._updateTitleIcon(
          lobbyDetails.name,
          lobbyDetails.setting.favoriteIcon
        );

        this._localeSettingService
          .updateLocaleSetting$({
            [defaultLocaleKey]: lobbyDetails.setting.supportedLocales[0],
            [supportedLocalesKey]: lobbyDetails.setting.supportedLocales
          })
          .pipe(take(1))
          .subscribe((localeSetting) => {
            this.updateDefaultLocale(localeSetting, activatedRouteUrl);
          });

        this._storeTheme(lobbyDetails.setting);
        this._storeLobbyTemplateId(lobbyDetails.lobbyTemplateId);
      }),
      tap(() => {
        if (deactivateLoadingOnEnd) {
          this._modalService.deactivateSolidModalBg();
          this._modalService.hideLoading();
        }
      }),
      catchError((err) => {
        if (onError) {
          onError(err);
        }

        if (deactivateLoadingOnEnd) {
          this._modalService.deactivateSolidModalBg();
          this._modalService.hideLoading();
        }

        return of(null);
      })
    );
  }

  getLobbyCategories$(onError?: Function): Observable<Category[]> {
    return this._apiService.getLobbyCategories$(this.locale).pipe(
      take(1),
      tap((lobbyCategories: Category[]) => {
        this._storeCategory(this.locale, lobbyCategories);
        this._gameService.setGameData(lobbyCategories);
      }),
      catchError((err) => {
        console.error(err);

        if (onError) {
          onError(err);
        }

        this._storeCategory(this.locale, []);
        return of([]);
      })
    );
  }

  showCategoriesPopup(categories: Category[]): void {
    const componentRef = this._componentFactoryResolver
      .resolveComponentFactory(CategoryContainerComponent)
      .create(this._injector);

    componentRef.instance.categories = categories;
    componentRef.instance.isPopup = true;
    componentRef.instance.back.subscribe(() => {
      this._appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });

    this._appRef.attachView(componentRef.hostView);

    const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
      .rootNodes[0] as HTMLElement;

    document.body.appendChild(domElem);
  }

  getSiteLocale(setting: LocaleSetting): string {
    if (setting[userSelectedLocaleKey]) {
      return setting[userSelectedLocaleKey];
    }

    if (
      (setting[supportedLocalesKey] || []).includes(
        setting[electronSystemLocaleKey]
      )
    ) {
      return setting[electronSystemLocaleKey];
    }

    if (
      (setting[supportedLocalesKey] || []).includes(setting[navigatorLocaleKey])
    ) {
      return setting[navigatorLocaleKey];
    }

    return setting[defaultLocaleKey] || environment.locale;
  }

  updateLocale(localeSetting: LocaleSetting): Locale {
    const siteLocale: Locale = this.getSiteLocale(localeSetting) as Locale;

    if (siteLocale) {
      this._translateService.setDefaultLang(siteLocale);
      this._setStorageLocale(siteLocale);
    }

    return siteLocale;
  }

  onSelectLocale$(
    localeCode: string,
    currentUrl: string,
    isLoggedIn: boolean
  ): Observable<null> {
    let obs$ = this._localeSettingService
      .updateLocaleSetting$({
        [userSelectedLocaleKey]: localeCode
      })
      .pipe(
        take(1),
        concatMap((_) =>
          this.getLobbyDetails$(currentUrl, () => {}, !isLoggedIn).pipe(
            take(1),
            map(() => null)
          )
        )
      );

    if (isLoggedIn) {
      obs$ = obs$.pipe(
        concatMap((_) =>
          this.getLobbyCategories$(null).pipe(
            take(1),
            map(() => null)
          )
        )
      );
    }

    return obs$;
  }

  updateDefaultLocale(
    localeSetting: LocaleSetting,
    activatedRouteUrl: string
  ): void {
    const siteLocale = this.updateLocale(localeSetting);

    if (!siteLocale) {
      return;
    }
    // trigger url renew with correct default language
    this._translateService
      .use(siteLocale)
      .pipe(take(1))
      .subscribe(() => {
        this.updateDefaultLanguageLoadStatus(true);
        this._localizeRouterService.translateRoute(activatedRouteUrl, true);
      });
  }

  refreshCategory(onError: Function): void {
    this._translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      const { lang } = event;
      this._setStorageLocale(lang as Locale);
      this._getDataAfterLangChange(event.lang as Locale, onError);
    });
  }

  updateDefaultLanguageLoadStatus(isLoaded: boolean): void {
    this._isDefaultLanguageLoaded.next(isLoaded);
  }

  private _setFaviconTitle(faviconTitle: FaviconTitle): void {
    const defaultFavicon = 'assets/images/icon/Lynx_128x128.ico';
    this._titleService.setTitle(faviconTitle.title);

    const imgUrl = faviconTitle.imgUrl ? faviconTitle.imgUrl : defaultFavicon;

    document.getElementById('favicon').setAttribute('href', imgUrl);

    const imgBase64 = faviconTitle.imgBase64
      ? faviconTitle.imgBase64
      : defaultFavicon;

    document
      .querySelectorAll('.favicon')
      .forEach((htmlLinkEl: HTMLLinkElement) =>
        htmlLinkEl.setAttribute('href', imgBase64)
      );

    this.addManifest(faviconTitle.imgUrl);

    this._faviconTitleMap[this._lobbyId] = faviconTitle;

    this._storageService.set(
      LYNX_STORAGE_KEYS.faviconTitle,
      this._faviconTitleMap,
      { type: StorageType.Local }
    );
  }

  private _getFaviconTitle(): void {
    this._lobbyId = this._storageService.get(
      LYNX_STORAGE_KEYS.lobbyId,
      StorageType.Session
    );

    this._faviconTitleMap =
      this._storageService.get(
        LYNX_STORAGE_KEYS.faviconTitle,
        StorageType.Local
      ) || {};

    const faviconTitle: FaviconTitle = this._faviconTitleMap[this._lobbyId];

    if (faviconTitle) {
      this._setFaviconTitle(faviconTitle);
    }
  }

  private _updateTitleIcon(title: string, imgUrl: string): void {
    ImageHelper.ImageToDataUrl(imgUrl, (imgBase64: string) => {
      const faviconTitle: FaviconTitle = {
        title,
        imgUrl,
        imgBase64
      };
      this._setFaviconTitle(faviconTitle);
    });
  }

  private _setStorageLocale(locale: Locale): void {
    this._storageService.set(LYNX_STORAGE_KEYS.locale, locale, {
      type: StorageType.Session
    });
  }

  private _storeCategory(currentLang: Locale, categories: Category[]): void {
    const tempObj = this._categorySubject.value;
    tempObj[currentLang] = categories;
    this._categorySubject.next(tempObj);
  }

  private _getDataAfterLangChange(currentLang: Locale, onError: Function) {
    if (
      !this._categorySubject.value ||
      !this._categorySubject.value[currentLang]
    ) {
      this.getLobbyCategories$(onError).subscribe();
    }
  }

  private _storeTheme(theme: LobbySetting): void {
    this._storageService.set(LYNX_STORAGE_KEYS.lobbyTheme, theme, {
      type: StorageType.Session
    });
    this._themeSubject.next(theme);
  }

  private _storeLobbyTemplateId(id: string): void {
    this._storageService.set(LYNX_STORAGE_KEYS.lobbyTemplateId, id, {
      type: StorageType.Session
    });
    this._lobbyTemplateIdSubject.next(id);
  }

  private addManifest(imgUrl: string): void {
    if (!imgUrl) {
      return;
    }
    const manifest = {
      name: 'Game Lobby',
      short_name: 'Game Lobby',
      display: 'standalone',
      background_color: '#ffffff',
      theme_color: '#000000',
      icons: [
        {
          src: imgUrl,
          sizes: '192x192',
          type: 'image/png'
        }
      ]
    };

    const blob = new Blob([JSON.stringify(manifest)], {
      type: 'application/json'
    });
    const manifestUrl = URL.createObjectURL(blob);
    const existingManifest = document.querySelector('link[rel="manifest"]');
    if (existingManifest) {
      document.head.removeChild(existingManifest);
    }
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = manifestUrl;
    document.head.appendChild(link);
  }
}
